import { BlockedUserScreenContainer } from '@/components/welcome/blocked-user-screen.container';
import PhoneNumberPreferencesDispatcher from '@/features/phone-number-preferences/phone-number-preferences-dispatcher';
import { BlockedUserError } from '@/lib/authentication';
import amplitudeService from '@/services/amplitude';
import { $apiAllo } from '@/services/api-allo/client';
import { newRelicService } from '@/services/newrelic';
import { supabase } from '@/services/supabase';
import type { User } from '@/types/user-types';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/(onboarding)/choose-number')({
  component: RouteComponent,
  onEnter: () => {
    amplitudeService.logEvent('Redirected to /choose-number');
  },
  beforeLoad: async ({ context }) => {
    const authSess = await supabase.auth.getSession();

    if (authSess.data.session === null || authSess.error) {
      throw redirect({ to: '/login', replace: true });
    }

    return context;
  },
  loader: async ({ context }) => {
    const { queryClient } = context as { queryClient: QueryClient };

    const userResponse = await queryClient.ensureQueryData(
      $apiAllo.queryOptions('get', '/v1/user', {}),
    );
    const user = userResponse?.data;

    if (user?.status === 'BLOCKED') {
      throw new BlockedUserError();
    }

    const businessResponse = await queryClient.ensureQueryData(
      $apiAllo.queryOptions('get', '/v1/business', {
        params: { query: { extend: 'sender_id' } },
      }),
    );
    const numbers = businessResponse?.data?.phone_numbers?.filter(
      (number) => number.number && number.number.trim() !== '',
    );
    if (numbers && numbers.length > 0 && numbers.some((number) => number.status === 'ACTIVE')) {
      newRelicService.addPageAction('onboarding:choose_number_loader:redirect_home', {
        domain: 'onboarding',
        phoneCount: numbers.length,
        activeCount: numbers.filter((n) => n.status === 'ACTIVE').length,
        unassignedCount: numbers.filter((n) => n.status === 'UNASSIGNED').length,
      });
      throw redirect({ to: '/', replace: true });
    }
    return user;
  },
  errorComponent: ({ error }) => {
    if (error instanceof BlockedUserError) {
      return <BlockedUserScreenContainer />;
    }
    throw error;
  },
});

function RouteComponent() {
  const user = Route.useLoaderData();

  return <PhoneNumberPreferencesDispatcher user={user as User} />;
}
